import { Component } from 'preact';
import { Mail, Swords } from 'lucide-react';
import { toast } from 'sonner';
import {
  ACCENT, ACCENT_BRIGHT, ACCENT_HI, ACCENT_LO, ACCENT_DEEP, ACCENT_TEXT,
  TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, GOLD_BTN, BEVELED_BTN, CONTENT_BG_DEFAULT,
} from '../../lib/medievalTheme';
import { TIER_HEX, TIER_LABELS, DIVISION_LABELS } from '../../utils/arena/rankUtils';
import AvatarImage from '../ui/AvatarImage';
import ChallengeMenu from '../ChallengeMenu';
import TreeFrame, { TREE_W, TREE_H } from './TreeFrame';
import { t } from '../../localization/t';

// The profile's hero: backdrop art, avatar plate, nameplate, rank
// medallion, record slab and the visitor's primary actions. Split out
// of FriendProfileOverlay so the composition can be read in one place.
//
// Design notes. The player's chosen Site is the most personal thing on
// the page, so it is shown sharp as a banner across the top and only
// blurred under the body where text has to sit on it. Shapes are kept
// organic: the banner ends in a torn edge rather than a ruled fade,
// the avatar sits in a rune-stone plate rather than a rounded square,
// and the record is one carved slab rather than four boxes.

// ── Torn edge ─────────────────────────────────────────────
// A deterministic jagged bottom edge for the banner, built once as an
// SVG mask. Seeded so it is the same tear on every open.
const RAGGED_MASK = (() => {
  let seed = 7;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    return seed / 2147483648;
  };
  // Long swells with short irregular ripples on top, joined by
  // quadratic curves through segment midpoints so the edge rolls
  // rather than zig-zags, then feathered with a small blur.
  const pts = [];
  for (let x = 1000; x >= 0; x -= 40) {
    const swell = 9 * Math.sin((x / 1000) * Math.PI * 2.6) + 4 * Math.sin((x / 1000) * Math.PI * 7.1);
    pts.push([x, 74 + swell + (rand() - 0.5) * 12]);
  }
  let d = `M0 0 H1000 V${pts[0][1].toFixed(1)}`;
  for (let i = 1; i < pts.length; i += 1) {
    const [cx, cy] = pts[i - 1];
    const [nx, ny] = pts[i];
    const mx = (cx + nx) / 2;
    const my = (cy + ny) / 2;
    d += ` Q${cx} ${cy.toFixed(1)} ${mx} ${my.toFixed(1)}`;
  }
  d += ` L0 ${pts[pts.length - 1][1].toFixed(1)} Z`;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 100' preserveAspectRatio='none'><filter id='f' x='0' y='0' width='1' height='1'><feGaussianBlur stdDeviation='0.9 1.6'/></filter><path d='${d}' fill='white' filter='url(#f)'/></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
})();

// The sharp art runs well past the hero and dissolves under the body
// panels over a long ramp, so there is no line where hero meets body.
const BANNER_MASK = `${RAGGED_MASK}, linear-gradient(180deg, black 0%, black 30%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.18) 80%, transparent 100%)`;

// `animateIn` (default true) plays the entrance zoom/fade on the sharp
// banner. The profile overlay passes false when the hub is already
// showing the same backdrop underneath, so opening the profile reads
// as one continuous background instead of the art re-entering.
export function HeroBackdrop({ art, animateIn = true }) {
  if (!art) return null;
  const washStyle = art.isSite
    ? {
        position: 'absolute', top: '50%', left: '50%', width: '100cqh', height: '100cqw',
        objectFit: 'cover', transform: 'translate(-50%, -50%) rotate(90deg) scale(1.08)',
      }
    : { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 18%', transform: 'scale(1.12)' };
  const bannerStyle = art.isSite
    ? {
        position: 'absolute', top: '50%', left: '50%', width: '100cqh', height: '100cqw',
        objectFit: 'cover', objectPosition: 'center 42%', transform: 'translate(-50%, -50%) rotate(90deg)',
      }
    : { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 12%' };
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Soft wash under the body. Heavier blur and darker than the
          old full-page treatment, since legibility is its only job. */}
      <div className="absolute inset-0" style={{ containerType: 'size', filter: 'blur(26px) saturate(1.05)', opacity: 0.52 }}>
        <img src={art.url} alt="" draggable={false} style={washStyle} />
      </div>
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 90% 75% at 50% 35%, rgba(8,6,4,0.08) 0%, rgba(8,6,4,0.45) 55%, rgba(8,6,4,0.82) 100%)' }}
      />
      {/* Sharp banner. Parallax offsets come from custom properties the
          overlay root writes on mouse move; the inner wrapper carries
          the entrance zoom and the slow drift. */}
      <div
        className="absolute left-0 right-0 top-0"
        style={{
          height: '66cqh',
          maskImage: BANNER_MASK,
          WebkitMaskImage: BANNER_MASK,
          maskSize: '100% 100%',
          WebkitMaskSize: '100% 100%',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in',
          transform: 'translate(calc(var(--hero-px, 0) * -0.6cqw), calc(var(--hero-py, 0) * -0.4cqw)) scale(1.05)',
          transition: 'transform 260ms cubic-bezier(0.2, 0.7, 0.2, 1)',
        }}
      >
        <div className={animateIn ? 'absolute inset-0 vh-profile-art-in' : 'absolute inset-0'} style={{ containerType: 'size' }}>
          <img src={art.url} alt="" draggable={false} style={bannerStyle} />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(8,6,4,0.8) 0%, transparent 22%, transparent 78%, rgba(8,6,4,0.8) 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,6,4,0.5) 0%, transparent 30%, transparent 70%, rgba(8,6,4,0.35) 100%)' }} />
      </div>
      {/* Dark backing for the nameplate and medallion. Lives here, on
          the page, rather than inside the hero: a band that ended at
          the hero's bottom edge read as a hard line against the body.
          The pools sit behind the identity cluster and the band keeps
          deepening under the panels. Stops are in canvas height so
          they track the hero's fixed position. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: [
            'radial-gradient(ellipse 42cqw 26cqh at 16cqw 24cqh, rgba(8,6,4,0.72) 0%, transparent 70%)',
            'radial-gradient(ellipse 22cqw 21cqh at 66cqw 26cqh, rgba(8,6,4,0.6) 0%, transparent 70%)',
            'linear-gradient(180deg, transparent 9cqh, rgba(8,6,4,0.3) 24cqh, rgba(8,6,4,0.52) 34cqh, rgba(8,6,4,0.6) 100%)',
          ].join(', '),
        }}
      />
      {/* Light spill. The art lights the stone below it in its own
          accent, which is what ties the recoloured chrome to the image. */}
      <div
        className="absolute left-0 right-0"
        style={{ top: '34cqh', height: '40cqh', background: `radial-gradient(ellipse 60% 55% at 50% 40%, ${ACCENT} 0.16) 0%, transparent 70%)` }}
      />
    </div>
  );
}

// ── Avatar plate ──────────────────────────────────────────
// Round portrait centred on the world tree. The ring escalates with
// rank: bare iron for an Apprentice, the accent for Adept and up,
// extra offset rings and a glow at Master and Grandmaster. Keyed by
// tier so a promotion visibly changes the player's plate.
const FRAME_BY_TIER = {
  apprentice: { stroke: 'rgba(166, 160, 155, 0.6)', rings: 0, glow: 0, hairline: false },
  journeyman: { stroke: 'rgba(212, 212, 216, 0.7)', rings: 0, glow: 0, hairline: false },
  adept: { stroke: ACCENT_TEXT, rings: 0, glow: 0.25, hairline: true },
  expert: { stroke: ACCENT_TEXT, rings: 1, glow: 0.35, hairline: true },
  master: { stroke: ACCENT_TEXT, rings: 1, glow: 0.55, hairline: true },
  grandmaster: { stroke: ACCENT_TEXT, rings: 2, glow: 0.75, hairline: true },
};

// Plate diameter and tree height, in cqw. The tree stays inside the
// hero and clear of the nameplate; the wrapper margins in the hero
// are derived from these.
export const PLATE_SIZE = 10.5;
export const TREE_SIZE = 15;

function AvatarPlate({ avatarUrl, name, tier, isOnline, onChange }) {
  const frame = FRAME_BY_TIER[tier] || FRAME_BY_TIER.apprentice;
  const size = `${PLATE_SIZE}cqw`;
  const treeW = TREE_SIZE * TREE_W / TREE_H;
  const treeStyle = {
    width: `${treeW}cqw`, height: `${TREE_SIZE}cqw`,
    left: `${(PLATE_SIZE - treeW) / 2}cqw`, top: `${(PLATE_SIZE - TREE_SIZE) / 2}cqw`,
    zIndex: -1,
  };

  const inner = avatarUrl ? (
    <AvatarImage src={avatarUrl} className="rounded-full" style={{ width: size, height: size }} />
  ) : (
    <div
      className="flex items-center justify-center font-bold arena-heading rounded-full"
      style={{
        width: size, height: size, fontSize: '2.5cqw', color: ACCENT_TEXT,
        background: 'radial-gradient(circle at 40% 35%, rgb(60, 44, 18) 0%, rgb(20, 14, 6) 70%)',
      }}
    >
      {(name || '?')[0].toUpperCase()}
    </div>
  );
  const Tag = onChange ? 'button' : 'div';
  return (
    <Tag
      type={onChange ? 'button' : undefined}
      title={onChange ? t('Change avatar') : undefined}
      className={`group relative shrink-0 block ${onChange ? 'cursor-pointer' : ''}`}
      style={{
        width: size, height: size,
        filter: frame.glow ? `drop-shadow(0 0 14px ${ACCENT} ${frame.glow}))` : 'drop-shadow(0 6px 12px rgba(0,0,0,0.6))',
      }}
      onClick={onChange || undefined}
    >
      <TreeFrame tier={tier} style={treeStyle} />
      {inner}
      {/* Ring. Non-scaling strokes keep the lines crisp at any canvas scale. */}
      <svg
        viewBox="-0.12 -0.12 1.24 1.24"
        className="absolute pointer-events-none"
        style={{ inset: '-12%', width: '124%', height: '124%' }}
        aria-hidden="true"
      >
        <circle cx="0.5" cy="0.5" r="0.5" fill="none" stroke="rgba(8,6,4,0.9)" strokeWidth="4" vectorEffect="non-scaling-stroke" />
        <circle cx="0.5" cy="0.5" r="0.5" fill="none" stroke={frame.stroke} strokeWidth="1.75" vectorEffect="non-scaling-stroke" />
        {frame.hairline ? (
          <circle cx="0.5" cy="0.5" r="0.47" fill="none" stroke={`${ACCENT_HI} 0.35)`} strokeWidth="0.75" vectorEffect="non-scaling-stroke" />
        ) : null}
        {frame.rings >= 1 ? (
          <circle cx="0.5" cy="0.5" r="0.535" fill="none" stroke={`${ACCENT} 0.5)`} strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ) : null}
        {frame.rings >= 2 ? (
          <circle cx="0.5" cy="0.5" r="0.575" fill="none" stroke={`${ACCENT} 0.3)`} strokeWidth="1" strokeDasharray="6 5" vectorEffect="non-scaling-stroke" />
        ) : null}
      </svg>
      {onChange ? (
        <div
          className="absolute inset-0 flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100 transition-opacity rounded-full overflow-hidden"
          style={{ background: 'linear-gradient(180deg, transparent 45%, rgba(8,6,4,0.85) 100%)' }}
        >
          <span className="text-[0.625cqw] font-semibold" style={{ color: ACCENT_TEXT, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{t('Change')}</span>
        </div>
      ) : null}
      {/* Presence dot on the rim at 45 degrees: the box corner sits
          outside a circle by r(1 - 1/sqrt 2), so inset by that minus
          half the dot. */}
      <div
        className="absolute rounded-full"
        style={{
          zIndex: 2,
          width: '1.2cqw', height: '1.2cqw',
          right: `${(PLATE_SIZE / 2) * (1 - Math.SQRT1_2) - 0.6}cqw`,
          bottom: `${(PLATE_SIZE / 2) * (1 - Math.SQRT1_2) - 0.6}cqw`,
          background: isOnline ? '#6ab04c' : 'rgba(120, 110, 100, 0.55)',
          border: '3px solid rgba(15, 12, 6, 0.98)',
          boxShadow: isOnline ? '0 0 10px rgba(106, 176, 76, 0.6)' : 'none',
        }}
      />
    </Tag>
  );
}

// ── Rank medallion ────────────────────────────────────────
// Tier ring in the tier colour with the division's LP as an arc around
// it. Replaces the old Rank Progress bar, which was empty for most
// players and took a whole panel to say so.
const MEDALLION_BG = `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, rgba(16,12,8,0.86) 0%, rgba(8,6,4,0.92) 100%)`;

function RankMedallion({ tier, division, lp }) {
  const hex = TIER_HEX[tier] || '#a8a29e';
  const label = TIER_LABELS[tier] || t('Unranked');
  const pct = Math.min(100, Math.max(0, lp || 0));
  const r = 44;
  const c = 2 * Math.PI * r;
  return (
    // The rise animation sits on the layers rather than on this row:
    // `.vh-profile-rise` fades from opacity 0, and opacity < 1 is a
    // backdrop root, which would blind the disc's backdrop-filter until
    // the entrance finished and snap the blur on. `--i` inherits, so
    // every layer still rises on the same beat.
    <div className="flex items-center gap-3 shrink-0" style={{ '--i': 2 }}>
      <div className="relative shrink-0" style={{ width: '5cqw', height: '5cqw' }}>
        {/* The disc is a DOM layer rather than the SVG's fill so it can
            carry the same blurred glass as the surrounding panels. */}
        <div
          className="absolute rounded-full pointer-events-none vh-profile-rise"
          style={{ inset: '3%', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', background: MEDALLION_BG, backgroundBlendMode: 'overlay, normal' }}
        />
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full vh-profile-rise" aria-hidden="true">
          <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(0,0,0,0.6)" strokeWidth="6" />
          <circle cx="50" cy="50" r={r} fill="none" stroke={`${hex}55`} strokeWidth="3.5" />
          {pct > 0 ? (
            <circle
              cx="50" cy="50" r={r} fill="none" stroke={hex} strokeWidth="3.5" strokeLinecap="round"
              strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(0.2, 0.7, 0.2, 1)' }}
            />
          ) : null}
          <circle cx="50" cy="50" r="36" fill="none" stroke={`${hex}33`} strokeWidth="1" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center arena-heading font-bold vh-profile-rise" style={{ color: hex, fontSize: '1.6cqw', textShadow: `0 0 12px ${hex}80, 0 2px 3px rgba(0,0,0,0.8)` }}>
          {DIVISION_LABELS[division] || ''}
        </div>
      </div>
      <div className="flex flex-col min-w-0 vh-profile-rise">
        <span className="font-bold truncate" style={{ color: hex, fontSize: '0.9375cqw', textShadow: '0 1px 3px rgba(0,0,0,0.95), 0 0 12px rgba(0,0,0,0.8)' }}>{label}</span>
        <span className="tabular-nums" style={{ color: TEXT_BODY, fontSize: '0.5625cqw', textShadow: '0 1px 3px rgba(0,0,0,0.95), 0 0 10px rgba(0,0,0,0.8)' }}>
          {division ? t('{pct} / 100 LP · Division {division}', { pct, division: DIVISION_LABELS[division] || division }) : t('{pct} / 100 LP', { pct })}
        </span>
      </div>
    </div>
  );
}

// ── Record slab ───────────────────────────────────────────
const SLAB_STYLE = {
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, rgba(16,12,8,0.86) 0%, rgba(8,6,4,0.92) 100%)`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${ACCENT} 0.42)`,
  borderRadius: 'var(--radius-lg)',
  boxShadow: [
    `inset 0 1px 0 ${ACCENT_HI} 0.1)`,
    `inset 0 0 0 1px ${ACCENT} 0.16)`,
    'inset 0 -2px 8px rgba(0,0,0,0.55)',
    'inset 0 0 36px rgba(0,0,0,0.5)',
    '0 8px 22px rgba(0,0,0,0.55)',
    `0 0 26px ${ACCENT} 0.1)`,
  ].join(', '),
};

function RecordSlab({ wins, losses, winRate, glicko }) {
  const cells = [
    { val: wins + losses, label: t('Matches'), color: TEXT_PRIMARY },
    { val: `${winRate}%`, label: t('Win rate'), color: ACCENT_TEXT },
    { val: wins, label: t('Wins'), color: '#7ad06a' },
    { val: losses, label: t('Losses'), color: '#e07060' },
  ];
  return (
    <div className="flex items-stretch vh-profile-rise" style={{ ...SLAB_STYLE, '--i': 3 }}>
      {typeof glicko?.rating === 'number' ? (
        <div className="flex flex-col justify-center px-4 py-2" style={{ borderRight: `1px solid ${ACCENT} 0.14)` }}>
          <span style={{ color: TEXT_MUTED, fontSize: '0.5cqw' }}>{t('Skill rating')}</span>
          <span className="font-bold tabular-nums arena-heading" style={{ color: ACCENT_TEXT, fontSize: '1.75cqw', lineHeight: 1.05, textShadow: `0 2px 4px rgba(0,0,0,0.6), 0 0 14px ${ACCENT_BRIGHT} 0.3)` }}>
            {Math.round(glicko.rating)}
            {typeof glicko.rd === 'number' ? (
              <span className="tabular-nums" style={{ color: TEXT_MUTED, fontSize: '0.625cqw', fontWeight: 500, marginLeft: '0.35cqw' }} title={t('Rating deviation. Shrinks as your rating converges on your true skill')}>±{Math.round(glicko.rd)}</span>
            ) : null}
          </span>
          <span style={{ color: `${ACCENT} 0.55)`, fontSize: '0.4375cqw', letterSpacing: '0.05em' }}>Glicko-2</span>
        </div>
      ) : null}
      {cells.map((s, i) => (
        <div
          key={s.label}
          className="flex flex-col items-center justify-center px-3 py-2"
          style={{ minWidth: '4cqw', borderRight: i < cells.length - 1 ? `1px solid ${ACCENT} 0.1)` : 'none' }}
        >
          <span className="font-bold tabular-nums" style={{ color: s.color, fontSize: '1.125cqw', lineHeight: 1, textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>{s.val}</span>
          <span className="mt-1" style={{ color: TEXT_MUTED, fontSize: '0.5cqw' }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Identity pills ────────────────────────────────────────
function Pill({ children, borderAlpha = 0.32, style }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[0.625cqw] font-semibold"
      style={{ background: 'rgba(20, 14, 6, 0.9)', border: `1px solid ${ACCENT} ${borderAlpha})`, borderRadius: 'var(--radius-md)', ...style }}
    >
      {children}
    </span>
  );
}

const DISCORD_PATH = 'M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z';

export default class ProfileHero extends Component {
  state = { challengeMenu: false };

  render() {
    const {
      profile, avatarUrl, displayedCrown, level, isOnline, statusText,
      wins, losses, winRate, onChangeAvatar, onChallenge, onMessage,
    } = this.props;
    const tier = profile.rank?.tier;
    return (
      <div className="relative shrink-0 px-8 pt-0 pb-8 z-10">
        <div className="relative flex items-center gap-6">
          {/* Margins reserve the tree's overhang (half the size
              difference on each side) so its box sits on the page's
              2cqw rhythm: 2cqw from the left edge, 2cqw above the
              panels, 2cqw before the nameplate. Its top lands 2cqw
              from the canvas top because the top bar is exactly
              padding + button tall. */}
          <div
            className="vh-profile-rise"
            style={{
              '--i': 0,
              margin: `0 ${(TREE_SIZE * TREE_W / TREE_H - PLATE_SIZE) / 2 + 0.5}cqw ${(TREE_SIZE - PLATE_SIZE) / 2}cqw ${(TREE_SIZE * TREE_W / TREE_H - PLATE_SIZE) / 2}cqw`,
            }}
          >
            <AvatarPlate avatarUrl={avatarUrl} name={profile.name} tier={tier} isOnline={isOnline} onChange={onChangeAvatar} />
          </div>

          <div className="flex-1 min-w-0 pb-2 vh-profile-rise" style={{ '--i': 1 }}>
            <h2
              className="arena-heading font-bold truncate"
              style={{
                fontSize: '2.75cqw', letterSpacing: '0.01em', lineHeight: 1,
                background: `linear-gradient(180deg, ${ACCENT_HI} 1) 0%, ${ACCENT_BRIGHT} 1) 55%, ${ACCENT_LO} 1) 100%)`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.85))',
              }}
            >
              {profile.name}{displayedCrown?.title ? ',' : ''}
            </h2>
            {displayedCrown?.title ? (
              <div className="mt-1 truncate text-[1cqw] italic" style={{ color: ACCENT_TEXT, letterSpacing: '0.02em', opacity: 0.92, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                {displayedCrown.title}
              </div>
            ) : null}
            <div className="flex items-center flex-wrap gap-1.5 mt-3">
              <Pill borderAlpha={0.45} style={{ color: ACCENT_TEXT }}>{t('Lv {level}', { level })}</Pill>
              <Pill borderAlpha={isOnline ? 0 : 0.25} style={{ border: `1px solid ${isOnline ? 'rgba(106, 176, 76, 0.4)' : `${ACCENT} 0.25)`}` }}>
                <span className="rounded-full" style={{ width: '0.4cqw', height: '0.4cqw', background: isOnline ? '#6ab04c' : 'rgba(120, 110, 100, 0.55)', boxShadow: isOnline ? '0 0 6px rgba(106, 176, 76, 0.8)' : 'none' }} />
                <span style={{ color: isOnline ? TEXT_BODY : TEXT_MUTED, fontWeight: 500 }}>{statusText || t('Offline')}</span>
              </Pill>
              {profile.discordHandle ? (
                <button
                  type="button"
                  title={t('Copy to clipboard')}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[0.625cqw] font-medium cursor-pointer transition-colors"
                  style={{ background: 'rgb(31, 28, 44)', border: '1px solid rgba(88, 101, 242, 0.4)', borderRadius: 'var(--radius-md)', color: '#a8b4ff' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgb(39, 38, 72)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgb(31, 28, 44)'; }}
                  onClick={() => {
                    navigator.clipboard.writeText(profile.discordHandle)
                      .then(() => toast.success(t('Discord handle copied')))
                      .catch(() => toast.error(t('Could not copy to clipboard')));
                  }}
                >
                  <svg style={{ width: '0.75cqw', height: '0.75cqw' }} viewBox="0 0 24 24" fill="#5865F2"><path d={DISCORD_PATH} /></svg>
                  @{profile.discordHandle}
                </button>
              ) : null}
            </div>
          </div>

          {/* Rank and record sit on the hero's bottom edge, resting on
              the panels below, instead of centring against the avatar
              plate where nothing else shares their midline. */}
          <div className="flex items-center gap-6 self-end">
          <RankMedallion tier={tier} division={profile.rank?.division} lp={profile.rank?.lp} />

          {/* Visitor actions hang above the slab, out of flow, so the
              slab sits in the same place on every profile. */}
          <div className="relative shrink-0">
            <RecordSlab wins={wins} losses={losses} winRate={winRate} glicko={profile.glicko} />
            {onChallenge || onMessage ? (
              <div className="absolute right-0 bottom-full mb-2 flex items-center gap-2 vh-profile-rise" style={{ '--i': 4 }}>
                {onMessage ? (
                  <button type="button" className="inline-flex items-center gap-1.5 px-3 text-[0.6875cqw] font-semibold cursor-pointer" style={{ ...BEVELED_BTN, backgroundColor: 'rgb(20, 14, 6)', height: '2cqw', color: ACCENT_TEXT }} onClick={onMessage}>
                    <Mail style={{ width: '0.8cqw', height: '0.8cqw' }} />
                    {t('Message')}
                  </button>
                ) : null}
                {onChallenge ? (
                  <div className="relative" onMouseLeave={() => this.state.challengeMenu && this.setState({ challengeMenu: false })}>
                    <button type="button" className="inline-flex items-center gap-1.5 px-3 text-[0.6875cqw] font-bold cursor-pointer" style={{ ...GOLD_BTN, height: '2cqw' }} onClick={() => this.setState((s) => ({ challengeMenu: !s.challengeMenu }))}>
                      <Swords style={{ width: '0.8cqw', height: '0.8cqw' }} />
                      {t('Challenge')}
                    </button>
                    {this.state.challengeMenu ? (
                      <ChallengeMenu
                        player={{ id: profile.id, name: profile.name }}
                        style={{ position: 'absolute', right: 0, top: '100%', marginTop: '0.25cqw', zIndex: 30 }}
                        onPick={(mode) => { this.setState({ challengeMenu: false }); onChallenge(mode); }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          </div>
        </div>
      </div>
    );
  }
}
